import { Model } from 'app/model/backendmodel';
import { IResourceFilesProvider, ICommitChange, IFullResource, ITagResult, ResourceProviderLocation } from 'app/interfaces/IResourceFilesProvider';
import { ModelRegisterationUtils } from 'app/model/services/model.utils/model.registration.utils';
import { stringsAreEqualCaseInsensitive, convertObjectToString } from 'app/shared/utils/string.util';
import { BaseResourceProvider } from 'app/services/resource-providers/base-resource-provider';
import { LoggerService } from 'app/logger/logger.service';
import { Metamodel } from 'app/model/base/model';
import { Dataset } from 'app/model/dataset/dataset';
import { LinkedService } from 'app/model/linkedservice/linkedservice';
import { IntegrationRuntime } from 'app/model/integrationRuntime/integrationRuntime';
import { ISaveResult } from '../../interfaces/ISaveStatus';
import { IResourceBase } from 'app/shared/utils/resourcefiles.utils';
import * as Constants from 'app/services/globals';
import { resolveResourceType } from 'app/shared/utils/model.concepts.utils';
import { ModelConstants, TreeNamedResAndNameUtils, SharedModel, Credential, LoadingStatus, GlobalVariables, IResourceContext, LoadingStatusEnum } from 'shell-models-lib';
import { getManagedVnetResource } from './managed-virtualnetwork.utils';
import { PrimaryResourceUrlInformationService } from '../primary-resource-url-information.service';
import { IFileClient } from '../interfaces/IFileClient';
import { BaseFileClientProviderService } from 'app/model/services/base-file-client-provider.service';
import { WranglingDataflow } from 'app/model/dataflow/wranglingDataflow';
import { ILiveResourceProviderService } from './ILiveResourceProvider';
import { Flowlet } from 'app/model/dataflow/flowlet';
import { msiCredentialName } from 'app/registries/utls/authenticationUtils';
import { UserDefinedFunctionLibrary } from 'app/model/dataflow/user-defined-function-library';
import { AutosaveService } from '../autosave/autosave.service';
import { IAzureResource } from 'app/interfaces/IAzureResource';
import { IdentityService } from 'app/model/services/identity.service';
import { ExposureControlService } from '../exposure-control/base-exposure-control.service';

interface IVNetResources {
    virtualNetworks: ITagResult[];
    privateEndpoints: ITagResult[];
}

export abstract class BaseFileResourceProviderService extends BaseResourceProvider implements IResourceFilesProvider {
    protected static readonly className: string = 'BaseFileResourceProviderService';

    protected lastVersionLoaded: string;

    private readonly _liveOnlyResources: Constants.ResourceType[] = [
        'sqlPool',
        'bigDataPool'
    ];

    private get _fileClient(): IFileClient {
        return this._fileClientProvider.client;
    }

    constructor(
        protected readonly _contextData: IResourceContext,
        private readonly _fileClientProvider: BaseFileClientProviderService<IFileClient>,
        protected readonly _loggerService: LoggerService,
        identityService: IdentityService,
        readonly _exposureControlService: ExposureControlService,
        protected readonly _autosaveService: AutosaveService,
        private readonly _primaryResourceUrlService: PrimaryResourceUrlInformationService,
        resourceProviderLocation: ResourceProviderLocation,
        private readonly _liveResourceProviderService?: ILiveResourceProviderService
    ) {
        super(_contextData, false, resourceProviderLocation, _loggerService, identityService, _exposureControlService, _autosaveService);
        if (__IS_A365__ && Constants.GlobalVariables.enableSynapseLink) {
            this._liveOnlyResources.push('linkconnection');
        }
    }

    public async commit(commitChanges: ICommitChange[], message?: string): Promise<ISaveResult> {
        // Currently WranglingDataFlow is a separate type in Madrid. In backend it is actually a subtype of DataFlow.
        // TODO: [parask] - Have MappingDataFlow and WranglingDataFlow as subtypes of DataFlow and, the model
        // should get created based on the subtype (similar to Datasets and LS). This change will go in as separate PR.
        // Resolve resource type, before saving.
        commitChanges.forEach((change) => {
            change.resource.type = resolveResourceType(change.resource.type);
        });
        // Update the commit information only if the last loaded commit with us was latest before committing.
        const existingVersion = await this.getLatestVersionId();
        const result = await this._fileClient.commit(commitChanges, message);
        if (this.lastVersionLoaded === existingVersion) {
            this.lastVersionLoaded = await this.getLatestVersionId();
        }
        return result;
    }

    public async getResourceObject(resourceType: Constants.ResourceType, resourceName: string): Promise<IFullResource> {
        for (const resource of this._resources.values()) {
            if (resource.name.toLowerCase() === resourceName.toLowerCase()) {
                return resource;
            }
        }
        return undefined;
    }

    public async getFolders(path: string): Promise<IResourceBase[]> {
        const result = await this._fileClient.getFoldersInFolder(path);
        return result;
    }

    public async getFile(path: string): Promise<string> {
        const result = await this._fileClient.getFileContent(path);
        return result;
    }

    public async waitClientInitialize(): Promise<void> {
        if (await this._fileClient.init()) {
            return;
        } else {
            throw new Error('Client service did not initialize');
        }
    }

    public updateEtag(_resourceType: Constants.ResourceType, _resourceName: string, _etag: string): void {
        // NO-OP
    }

    protected abstract getLatestVersionId(): Promise<string>;

    protected async onInit(): Promise<void> {
        this._resources.clear();
    }

    protected async repopulateResourceByType(fullLoad: boolean, resourceType: Constants.ResourceType): Promise<void> {
        this._fileClient.getFilesInFolder(resourceType);
    }

    protected async populateAllResources(fullLoad: boolean): Promise<void> {
        let isMSICredentialFromGit: boolean = false;
        let isResourceTypeCredential: boolean = false;

        if (this._fileClient === undefined || !await this._fileClient.init()) {
            this._loggerService.warn(BaseFileResourceProviderService.className, 'populateAllResources - failed. FileClient did not initialize');
            throw new Error('Git Client service did not initialize');
        }
        this._loggerService.log(BaseFileResourceProviderService.className, 'populateAllResources - start');
        const irPromise = this._fileClient.getFilesInFolder('integrationRuntime');
        const resourceListPromises = [
            this._fileClient.getFilesInFolder('pipeline'),
            this._fileClient.getFilesInFolder('dataset'),
            this._fileClient.getFilesInFolder('linkedService'),
            this._fileClient.getFilesInFolder('trigger'),
            irPromise,
            this._fileClient.getFilesInFolder('factory'),
            this._fileClient.getFilesInFolder('dataflow'),
            this._fileClient.getFilesInFolder('credential'),
            this._fileClient.getFilesInFolder('adfcdc'),
            this._fileClient.getFilesInFolder('managedVirtualNetwork'),
        ];

        if (Constants.GlobalVariables.airflowEntity && !Constants.GlobalVariables.enableIFrameMode) {
            resourceListPromises.push(
                this._fileClient.getFilesInFolder('airflowEntity')
            );
        }

        if (__IS_A365__) {
            resourceListPromises.push(
                this._fileClient.getFilesInFolder('sqlscript'),
                this._fileClient.getFilesInFolder('kqlscript'),
                this._fileClient.getFilesInFolder('sparkJobDefinition'),
                this._fileClient.getFilesInFolder('scopeJobDefinition')
            );

            if (GlobalVariables.isCmdApiApp) {
                resourceListPromises.push(this._fileClient.getFilesInFolder('bigDataPool'));
                resourceListPromises.push(this._fileClient.getFilesInFolder('sqlPool'));
            }

            if (Constants.GlobalVariables.enableAutosave) {
                resourceListPromises.push(this.fetchResources('notebook'));
            } else {
                resourceListPromises.push(this._fileClient.getFilesInFolder('notebook'));
            }

            resourceListPromises.push(this._fileClient.getFilesInFolder('sparkConfiguration'));

            resourceListPromises.push(this._getDatabaseResources());

            // In Synapse, certain resource types are not stored in the Git repo, so they must be fetched from backend.
            if (this._liveResourceProviderService) {
                resourceListPromises.push(
                    this._liveResourceProviderService
                        .getResourceProvider(true)
                        .populateResources(...this._liveOnlyResources));
            }
        }

        const gitResources: IFullResource[] = await [].concat.apply([], await Promise.all(resourceListPromises));

        this._loggerService.log(BaseFileResourceProviderService.className, 'populateAllResources - downloaded git resources');

        this.lastVersionLoaded = await this.getLatestVersionId();
        this._loggerService.log(BaseFileResourceProviderService.className, 'populateAllResources - loaded last version id');

        for (const resource of gitResources) {
            await this._processResource(resource);

            if (resource.type === Credential.MODEL.name) {
                isResourceTypeCredential = true;
            }

            // in the list of resources received from backend and being iterated over, check if the SystemMSI cred exists
            if (resource.type === Credential.MODEL.name && resource.name === msiCredentialName) {
                isMSICredentialFromGit = true;
            }
        }

        const vnetResourcesPromise: Promise<IVNetResources> = this._getManagedPrivateEndpointResources();
        if (vnetResourcesPromise) {
            const { virtualNetworks, privateEndpoints } = await vnetResourcesPromise;
            const vnetResources = [...virtualNetworks, ...privateEndpoints] as IFullResource[];
            if (virtualNetworks.length) {
                for (const resource of vnetResources) {
                    await this._processResource(resource);
                }
            } else {
                // No VNets have been imported to git, but default vnet may exist which the default IR would indicate.
                // This occurs when the factory is created with VNET and Git opted in.
                const irList = await irPromise as IFullResource[];
                const isVNetEnabled = this.updateVnetEnabledFlag(irList);

                if (isVNetEnabled) {
                    await this._insertDefaultVNet();
                }
            }
        }

        // if the call to this function is for credentials, and in the list of credentials received from the backend, we don't see the system MSI cred,
        // create a dummy UX model of the credential. Publish of this will be handled in the respective publish layer of GIT and Live mode.
        if (!__IS_A365__ && Constants.GlobalVariables.isMsiBackfillEnabled && isResourceTypeCredential && !isMSICredentialFromGit) {
            await this._addSystemMsiCredential();
        }

        this._loggerService.log(BaseFileResourceProviderService.className, 'populateAllResources - finished');

        this.resourceSubject.next(Array.from(this._resources.values()));
    }

    private async _processResource(resource: IFullResource): Promise<void> {
        let loadingError: LoadingStatus = LoadingStatusEnum.UnknownError;

        try {
            if (!resource) {
                this._loggerService.warn(BaseFileResourceProviderService.className, '_processResource - resource not defined, skipping');
                return;
            }

            let resourceAsAny;

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                resourceAsAny = JSON.parse(resource.payload);
            } catch (error) {
                // invalid payload, mark the resource as invalid
                throw loadingError = LoadingStatusEnum.InvalidJson;
            }

            if (!stringsAreEqualCaseInsensitive(resource.name, resourceAsAny.name)) {
                throw loadingError = LoadingStatusEnum.NameMismatch;
            }

            resource.name = this._correctResourceName(resource);
            resource.folderName = resourceAsAny.properties && resourceAsAny.properties.folder ? resourceAsAny.properties.folder.name : undefined;
            resource.tags = resourceAsAny.properties ? resourceAsAny.properties.annotations : undefined;
            // we must know the subtypes for datasets, linked services and IR
            if ([Metamodel.modelFor(Dataset).name, Metamodel.modelFor(LinkedService).name, Metamodel.modelFor(IntegrationRuntime).name].includes(resource.type)) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                if (!resourceAsAny.properties || !(resourceAsAny.properties as any).type) {
                    throw loadingError = LoadingStatusEnum.MissingSubType;
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                resource.subType = (resourceAsAny.properties as any).type;
            }

            // Currently WranglingDataFlow is a separate type in Madrid. In backend it is actually a subtype of DataFlow.
            // TODO: [parask] - Have MappingDataFlow and WranglingDataFlow as subtypes of DataFlow and, the model
            // should get created based on the subtype (similar to Datasets and LS). This change will go in as separate PR
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (resourceAsAny.properties && (resourceAsAny.properties as any).type && (resourceAsAny.properties as any).type.toLowerCase() === 'wranglingdataflow') {
                resource.type = WranglingDataflow.MODEL.name;
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (resourceAsAny.properties && (resourceAsAny.properties as any).type && (resourceAsAny.properties as any).type.toLowerCase().includes(Flowlet.MODEL.name.toLowerCase())) {
                resource.subType = Flowlet.MODEL.name;
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (resourceAsAny.properties && (resourceAsAny.properties as any).type && (resourceAsAny.properties as any).type.toLowerCase() === UserDefinedFunctionLibrary.MODEL.name.toLowerCase()) {
                resource.subType = UserDefinedFunctionLibrary.MODEL.name;
            }
            resource.status = LoadingStatusEnum.Ok;

            // if a resource has schema property, treat is as v2 resource
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (resourceAsAny.properties && (resourceAsAny.properties as any).schema != null) {
                resource.isV2 = true;
            }
        } catch (error) {
            resource.status = loadingError;
            if ((loadingError as LoadingStatus) === 'UnknownError') {
                this._loggerService.error(BaseFileResourceProviderService.className,
                    `_processResource - Failed to load resource, loadingError: ${loadingError}, Error: ${convertObjectToString(error)}.`,
                    {
                        resourceName: resource.name,
                        resourceType: resource.type,
                        resourceSubType: resource.subType
                    });
            } else {
                this._loggerService.warn(BaseFileResourceProviderService.className,
                    `_processResource - Failed to load resource, loadingError: ${loadingError}, Error: ${convertObjectToString(error)}.`,
                    {
                        resourceName: resource.name,
                        resourceType: resource.type,
                        resourceSubType: resource.subType
                    });
            }
        } finally {
            if ([Metamodel.modelFor(Dataset).name, Metamodel.modelFor(LinkedService).name].includes(resource.type)) {
                resource.subType = resource.subType || 'unknown';
                await ModelRegisterationUtils.registerUnknownSubtype(resource.type, resource.subType, this._contextData);
            }

            this._resources.set(this.identityService.generateIdentifierFromResource(resource), resource);
        }
    }

    private _correctResourceName(resource: IFullResource): string {
        if (resource.type && Constants.subResourceTypeMap.database.includes(resource.type as Constants.ResourceType) && resource.parentResource) {
            return TreeNamedResAndNameUtils.correctName(resource.parentResource.name, resource.name);
        }
        return resource.name;
    }

    private async _getManagedPrivateEndpointResources(): Promise<IVNetResources> {
        const vnetResources: IVNetResources = {
            virtualNetworks: [],
            privateEndpoints: []
        };

        const files = await this._fileClient.getFilesInFolder(ModelConstants.managedVirtualNetwork);
        vnetResources.virtualNetworks = files.filter((file) => file.type === ModelConstants.managedVirtualNetwork);
        const vnetFolders = await this._fileClient.getFoldersInFolder(ModelConstants.managedVirtualNetwork);
        for (const vnetFolder of vnetFolders) {
            const vnetResource: Model.ManagedVirtualNetworkResource = {
                name: vnetFolder.name,
                type: ModelConstants.managedVirtualNetwork
            };

            if (!vnetResources.virtualNetworks.find((vnet) => vnet.name === vnetResource.name)) {
                vnetResources.virtualNetworks.push({
                    name: vnetResource.name,
                    payload: JSON.stringify(vnetResource),
                    type: ModelConstants.managedVirtualNetwork
                });
            }

            const vnetPath = `${ModelConstants.managedVirtualNetwork}/${vnetFolder.name}/${ModelConstants.managedPrivateEndpoint}`;
            await this._fileClient.getFilesInFolder(vnetPath).then((peFiles) => {
                if (peFiles) {
                    peFiles.forEach((privateEndpoint: IResourceBase) => {
                        privateEndpoint.parentResource = {
                            name: vnetFolder.name,
                            resourceType: ModelConstants.managedVirtualNetwork
                        };

                        try {
                            // Add Virtual Network reference as this is not part of the private endpoint model.
                            const peResource = JSON.parse((privateEndpoint as ITagResult).payload) as Model.ManagedPrivateEndpointResource;
                            if (peResource.properties) {
                                peResource.properties.managedVirtualNetwork = {
                                    type: 'ManagedVirtualNetworkReference',
                                    referenceName: vnetFolder.name
                                };
                                (privateEndpoint as ITagResult).payload = JSON.stringify(peResource);
                            }
                        } catch (e) {
                            this._loggerService.warn(
                                BaseFileResourceProviderService.className,
                                'Failed to set the managed virutal network reference for a managed private endpoint: ' + convertObjectToString(e));
                        }
                    });
                    vnetResources.privateEndpoints.push(...peFiles);
                }
            });
        }
        return vnetResources;
    }

    private async _getDatabaseResources(): Promise<ITagResult[]> {
        try {
            const databaseFiles: ITagResult[] = await this._fileClient.getFilesInFolder(ModelConstants.database);

            if (databaseFiles.find((file: ITagResult) => file.type === ModelConstants.table)) {
                // If the table has been fetched, return directly
                return databaseFiles;
            }
            const subResourceTypes = Constants.subResourceTypeMap[ModelConstants.database];
            const filesPromises: Array<Promise<ITagResult[]>> = [];
            subResourceTypes.forEach((subType: string) => {
                databaseFiles.forEach((dbFile: ITagResult) => {
                    const promise: Promise<ITagResult[]> = this._fileClient.getFilesInFolder(`${ModelConstants.database}/${dbFile.name}/${subType}`);
                    filesPromises.push(promise);
                });
            });
            const entitiesFiles: ITagResult[][] = await Promise.all(filesPromises);
            const results: ITagResult[] = [...databaseFiles];
            entitiesFiles.forEach((files: ITagResult[]) => results.push(...files));

            return results;
        } catch (error) {
            this._loggerService.warn(BaseFileResourceProviderService.className,
                `_getDatabaseResources - Failed to load resource, Error: ${convertObjectToString(error)}.`);
        }
        return [];
    }

    private async _insertDefaultVNet(): Promise<void> {
        // No VNET folders when repo is new
        const vnetResource = getManagedVnetResource(this._primaryResourceUrlService.service.resourceId);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const vnetFullResource: any = {
            name: vnetResource.name,
            payload: JSON.stringify(vnetResource),
            type: ModelConstants.managedVirtualNetwork
        };
        await this._processResource(vnetFullResource);
    }

    private async _addSystemMsiCredential(): Promise<void> {

        const systemMsiCredentialPayload: SharedModel.CredentialResource = {
            name: msiCredentialName,
            properties: {
                type: 'ManagedIdentity',
                typeProperties: undefined
            },
            isUxModel: true
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const systemMsiCredFullResource: any = {
            name: msiCredentialName,
            payload: JSON.stringify(systemMsiCredentialPayload),
            type: ModelConstants.credential
        };
        await this._processResource(systemMsiCredFullResource);
    }

    private async fetchResources(resourceType: Constants.ResourceType): Promise<IFullResource[]> {
        const gitResourcesPromise = this._fileClient.getFilesInFolder(resourceType);
        const userStoreResourcesPromise = this.getResourcesInUserStore(resourceType);
        const gitResources: IFullResource[] = (await gitResourcesPromise).map((resource: ITagResult) => {
            return {
                ...resource, status: LoadingStatusEnum.Ok
            };
        });
        const userStoreResources: IFullResource[] = await userStoreResourcesPromise;
        const mergedResources = this.mergeResources(userStoreResources, gitResources, resourceType);
        return mergedResources;
    }

    private async getResourcesInUserStore(resourceType: Constants.ResourceType): Promise<IFullResource[]> {
        // try to fetch resources in user store
        const privateResourcesPromise = Constants.GlobalVariables.enableAutosave && await this._autosaveService.isResourceTypeEnabled(resourceType)
            ? this.fetchResourcesInUserStore(resourceType) : Promise.resolve([]);
        const resources: IAzureResource[] = await privateResourcesPromise;
        const updatedResources: IFullResource[] = this._autosaveResourceProvider.convertAzureResourcesToFullResources(resources);
        return updatedResources;
    }
}
